"use client";

import { useState, type MouseEvent } from "react";
import { ArrowRight, Check, Clock3, ExternalLink, FileText, ListTodo, RefreshCw } from "lucide-react";
import { emptyFocusFilters, focusFiltersKey, hasFocusFilters, type FocusFilterFacet, type FocusFilterOptions, type FocusFilters, type FocusPeriod, type FocusTask, type PersonalFocusModel } from "../../lib/personalFocus";
import type { FocusEvidence } from "../../lib/personalFocusEvidence";
import { formatTaskDate } from "../../lib/taskDates";
import { cn } from "../../lib/utils";

export interface PersonalFocusProps {
  model: PersonalFocusModel | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  workspaceSlug: string;
  onOpenTask: (id: string) => void;
  onConfirm: (id: string) => void;
  onClear: () => void;
  onRetry: () => void;
  onNavigateTasks: () => void;
  onShowContext: () => void;
  evidence?: FocusEvidence[];
  period?: FocusPeriod;
  onPeriodChange?: (period: FocusPeriod) => void;
  filters?: FocusFilters;
  onFiltersChange?: (filters: FocusFilters) => void;
  filterOptions?: FocusFilterOptions;
  variant?: "compact" | "expanded";
}

const controlClass = "focus-ring inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 rounded-md px-3 text-[12px] font-medium transition-colors disabled:cursor-wait disabled:opacity-50";
const secondaryControl = `${controlClass} border border-[var(--border)] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]`;
const primaryControl = `${controlClass} bg-[var(--accent)] text-white hover:opacity-90`;
const priorityNames = { 0: "Sem prioridade", 1: "Urgente", 2: "Alta", 3: "Média", 4: "Baixa" };
const NEXT_TASK_PAGE_SIZE = 6;

function selectedPeriod(props: PersonalFocusProps): FocusPeriod {
  return props.period ?? props.model?.period ?? 30;
}

function periodLabel(period: FocusPeriod): string {
  return period === "all" ? "Todos" : `Últimos ${period} dias`;
}
const selectedFilters = (props: PersonalFocusProps) => props.filters ?? props.model?.filters ?? emptyFocusFilters();
const paginationKey = (props: PersonalFocusProps) => `${props.workspaceSlug}:${props.model?.primary?.id ?? "none"}:${selectedPeriod(props)}:${focusFiltersKey(selectedFilters(props))}`;
const FACET_LABELS: Record<FocusFilterFacet, string> = { projectIds: "Projeto", labelIds: "Temas (labels)", origins: "Origem", slackChannels: "Canal Slack registrado" };

function FocusFilterControls(props: PersonalFocusProps) {
  const filters = selectedFilters(props);
  return (
    <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-2">
      <label className="min-w-0 text-[11px] text-[var(--fg-tertiary)] sm:col-span-2">
        <span className="mb-1 block">Tema por texto</span>
        <input type="search" aria-label="Tema por texto" maxLength={500} value={filters.query}
          disabled={!props.onFiltersChange} placeholder="Buscar no título e na descrição visível"
          onChange={(event) => props.onFiltersChange?.({ ...filters, query: event.currentTarget.value })}
          className="focus-ring min-h-11 w-full min-w-0 rounded-md border border-[var(--border)] bg-[var(--bg)] px-3 text-[12px] text-[var(--fg-secondary)] disabled:opacity-60" />
      </label>
      {(Object.keys(FACET_LABELS) as FocusFilterFacet[]).map((facet) => {
        const options = props.filterOptions?.[facet] ?? [];
        const missing = filters[facet].filter((value) => !options.some((option) => option.value === value))
          .map((value) => ({ value, label: "Seleção registrada indisponível" }));
        return (
          <details key={facet} className="min-w-0 rounded-md border border-[var(--border)] bg-[var(--bg)]">
            <summary className="focus-ring min-h-11 cursor-pointer break-words rounded-md px-3 py-3 text-[12px] font-medium text-[var(--fg-secondary)]">
              {FACET_LABELS[facet]} · {filters[facet].length ? `${filters[facet].length} selecionado(s)` : "Todos"}
            </summary>
            <fieldset disabled={!props.onFiltersChange} className="max-h-64 min-w-0 overflow-y-auto border-t border-[var(--border)] px-2 py-1">
              <legend className="sr-only">{FACET_LABELS[facet]}</legend>
              {[...options, ...missing].map((option) => (
                <label key={option.value} className="flex min-h-11 min-w-0 cursor-pointer items-center gap-2 rounded px-1 text-[12px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]">
                  <input type="checkbox" className="focus-ring h-4 w-4 shrink-0 accent-[var(--accent)]"
                    checked={filters[facet].includes(option.value)}
                    aria-label={`${FACET_LABELS[facet]}: ${option.label}`}
                    onChange={(event) => props.onFiltersChange?.({ ...filters, [facet]: event.currentTarget.checked
                      ? [...filters[facet], option.value] : filters[facet].filter((value) => value !== option.value) })} />
                  <span className="min-w-0 break-words">{option.label}</span>
                </label>
              ))}
              {!options.length && !missing.length && <p className="px-1 py-3 text-[11px] text-[var(--fg-muted)]">Nenhuma opção registrada na sua fila pessoal.</p>}
            </fieldset>
          </details>
        );
      })}
      {hasFocusFilters(filters) && <button type="button" disabled={!props.onFiltersChange} className={secondaryControl}
        onClick={() => props.onFiltersChange?.(emptyFocusFilters())}>Limpar filtros</button>}
      <p className="text-[11px] leading-5 text-[var(--fg-muted)] sm:col-span-2">Filtros combinados; opções do mesmo filtro são alternativas. Busque um tema por texto ou labels registradas. Canais Slack só entram pelo link registrado, com nomes genéricos; o link não confirma seu acesso.</p>
      {props.model && !props.loading && !props.error && props.filterOptions && !props.filterOptions.slackChannels.some((option) => option.value !== "__none__") &&
        <p className="text-[11px] leading-5 text-[var(--fg-muted)] sm:col-span-2">Nenhum canal Slack identificado por link na sua fila. Use origem Slack ou canal não identificado; texto, projeto e labels continuam disponíveis.</p>}
    </div>
  );
}

/** Presentation of an existing, assigned task; it never creates or reprioritizes tasks. */
export function PersonalFocus(props: PersonalFocusProps) {
  const { variant = "expanded", model, loading, error } = props;
  if (variant === "compact") return <CompactFocus {...props} />;

  return (
    <section aria-label="Seu foco agora" aria-busy={loading || props.saving} className="mb-6 min-w-0 rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)] p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[17px] font-semibold tracking-[-0.01em]">Seu foco agora</h2>
        {model?.primary && <ModeBadge model={model} />}
      </div>
      <label className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-[var(--fg-tertiary)]">
        <span>Período de criação</span>
        <select
          aria-label="Período de criação"
          value={String(selectedPeriod(props))}
          disabled={!props.onPeriodChange}
          onChange={(event) => {
            const value = event.currentTarget.value;
            if (value === "all") props.onPeriodChange?.("all");
            else if (value === "7" || value === "30" || value === "90") props.onPeriodChange?.(Number(value) as FocusPeriod);
          }}
          className="focus-ring min-h-11 max-w-full rounded-md border border-[var(--border)] bg-[var(--bg)] px-3 text-[12px] text-[var(--fg-secondary)] disabled:opacity-60"
        >
          <option value="7">Últimos 7 dias</option>
          <option value="30">Últimos 30 dias</option>
          <option value="90">Últimos 90 dias</option>
          <option value="all">Todos</option>
        </select>
      </label>
      <FocusFilterControls {...props} />
      {model?.counts && <p role="status" className="mt-2 text-[11px] leading-5 text-[var(--fg-tertiary)]">{model.counts.matching} de {model.counts.total} tarefas abertas correspondem à combinação de filtros.</p>}
      <p className="mt-1 text-[12px] leading-5 text-[var(--fg-tertiary)]">
        Contexto registrado no Pauta. O foco confirmado fica salvo até você trocar ou o ticket deixar de estar disponível para você.
      </p>

      {loading && !model ? (
        <p role="status" className="mt-4 text-[13px] text-[var(--fg-tertiary)]">Carregando seu foco…</p>
      ) : error && !model ? (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <p role="alert" className="text-[13px] text-[var(--fg-secondary)]">Não foi possível carregar seu foco.</p>
          <button type="button" onClick={props.onRetry} className={secondaryControl}>
            <RefreshCw size={14} aria-hidden="true" />Tentar novamente
          </button>
        </div>
      ) : model?.primary ? (
        <FocusContent {...props} model={model} />
      ) : (
        <div className="mt-4">
          {model?.previousFocusUnavailable && <UnavailableNotice />}
          <p className="text-[13px] leading-6 text-[var(--fg-secondary)]">{model?.reason || (selectedPeriod(props) === "all" ? "Nenhum ticket aberto atribuído a você neste workspace." : "Sem sugestão no período de criação selecionado.")}</p>
          <button type="button" onClick={props.onNavigateTasks} className={`${secondaryControl} mt-3`}>
            <ListTodo size={14} aria-hidden="true" />Ver tarefas
          </button>
          {model && <NextFocusGroups key={paginationKey(props)} {...props} model={model} />}
        </div>
      )}

      {error && model && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <p role="alert" className="min-w-0 flex-1 text-[12px] leading-5 text-[var(--orange)]">{error}</p>
          <button type="button" disabled={loading || props.saving} onClick={props.onRetry} className={secondaryControl}>
            <RefreshCw size={14} aria-hidden="true" />Tentar novamente
          </button>
        </div>
      )}
      <p className="mt-4 border-t border-[var(--border)] pt-3 text-[11px] leading-5 text-[var(--fg-muted)]">
        Slack, notas de reunião no Drive e Loom só aparecem aqui quando já incorporados ao ticket. Esta visão não lê essas fontes automaticamente.
      </p>
    </section>
  );
}

function CompactFocus(props: PersonalFocusProps) {
  const { model, loading, error } = props;
  return (
    <section aria-label="Seu foco agora" aria-busy={loading || props.saving} className="min-w-0 shrink-0 border-b border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-1.5">
      <div className="flex min-h-4 flex-wrap items-center gap-x-2 text-[10px] leading-4">
        <span className="font-medium text-[var(--fg-tertiary)]">Seu foco agora</span>
        <span className="text-[var(--fg-muted)]">{periodLabel(selectedPeriod(props))}</span>
        {hasFocusFilters(selectedFilters(props)) && <span className="text-[var(--fg-muted)]">Filtros ativos</span>}
        {model?.primary && <ModeBadge model={model} compact />}
        {model?.waiting && !model.primaryOutsidePeriod && <span className="text-[var(--orange)]">{model.primary?.status === "in_review" ? "Em revisão" : "Dependência"}</span>}
      </div>
      <div className="flex min-h-11 min-w-0 items-center gap-1">
        {loading && !model ? (
          <span role="status" className="min-w-0 flex-1 truncate text-[12px] text-[var(--fg-tertiary)]">Carregando seu foco…</span>
        ) : error && !model ? (
          <>
            <span role="alert" className="min-w-0 flex-1 text-[12px] text-[var(--fg-secondary)]">Foco indisponível</span>
            <button type="button" onClick={props.onRetry} className={controlClass}>Tentar novamente</button>
          </>
        ) : model?.primary ? (
          <>
            <TaskLink
              task={model.primary}
              workspaceSlug={props.workspaceSlug}
              onOpenTask={props.onOpenTask}
              className="min-h-11 min-w-0 flex-1 rounded-md py-0.5 text-[12px] leading-4"
              reason={error ? "Não foi possível atualizar seu foco. Ver contexto." : model.reason}
              dense
            />
            <button type="button" onClick={props.onShowContext} className={`${controlClass} px-2 text-[var(--accent)] hover:bg-[var(--bg-hover)]`}>
              Ver contexto<ArrowRight size={13} aria-hidden="true" />
            </button>
          </>
        ) : (
          <>
            <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--fg-tertiary)]">
              {hasFocusFilters(selectedFilters(props)) ? "Sem sugestão nos filtros" : selectedPeriod(props) !== "all" ? "Sem sugestão no período" : model?.nextByProject.some((group) => group.tasks.length > 0) ? "Sem sugestão automática" : "Sem ticket aberto atribuído a você"}
            </span>
            <button type="button" onClick={props.onNavigateTasks} className={`${controlClass} px-2 text-[var(--accent)]`}>Ver tarefas</button>
          </>
        )}
      </div>
    </section>
  );
}

function FocusContent(props: PersonalFocusProps & { model: PersonalFocusModel }) {
  const { model, saving, evidence = [] } = props;
  const task = model.primary!;
  return (
    <>
      {model.previousFocusUnavailable && <UnavailableNotice />}
      {model.primaryOutsidePeriod && model.mode === "confirmed" && (
        <p className="mt-3 text-[12px] leading-5 text-[var(--orange)]">Foco confirmado fora do período. Sua escolha foi mantida; você pode trocar o ticket ou remover a confirmação.</p>
      )}
      {model.primaryOutsideFilters && model.mode === "confirmed" && (
        <p className="mt-3 text-[12px] leading-5 text-[var(--orange)]">Foco confirmado fora dos filtros de texto, projeto, tema, origem ou canal. Sua escolha foi mantida; você pode trocar o ticket ou remover a confirmação.</p>
      )}
      <div className="mt-4 rounded-md border border-[var(--border)] bg-[var(--bg)] p-3 sm:p-4">
        <TaskLink
          task={task}
          workspaceSlug={props.workspaceSlug}
          onOpenTask={props.onOpenTask}
          className="block min-h-11 rounded-md text-[15px] leading-6"
          expanded
        />
        <p className="mt-1 text-[11px] leading-5 text-[var(--fg-tertiary)]">
          Prioridade registrada: {priorityNames[task.priority]} · {task.dueDate !== null && Number.isFinite(task.dueDate) ? `Prazo registrado: ${new Date(task.dueDate).toISOString().slice(0, 10).split("-").reverse().join("/")}` : "Sem prazo registrado"}
        </p>
        {task.status === "backlog" && <p className="mt-1 text-[11px] text-[var(--fg-tertiary)]">Backlog · foco escolhido manualmente</p>}
        <p className="mt-2 text-[13px] leading-6 text-[var(--fg-secondary)]">{model.reason}</p>
        {model.waiting && (
          <p className="mt-2 flex items-start gap-1.5 text-[12px] leading-5 text-[var(--orange)]">
            <Clock3 size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            {task.status === "in_review"
              ? "Precisa de revisão ou decisão. Abra o ticket para conferir quem deve revisar."
              : "Dependência registrada pendente ou sem estado verificável. Abra o ticket para conferir o que falta antes de executar."}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {model.mode === "suggested" ? (
            <button type="button" disabled={saving} onClick={() => props.onConfirm(task.id)} className={primaryControl}>
              <Check size={14} aria-hidden="true" />{saving ? "Salvando…" : "Confirmar foco"}
            </button>
          ) : (
            <button type="button" disabled={saving} onClick={props.onClear} className={secondaryControl}>
              {saving ? "Salvando…" : "Remover confirmação"}
            </button>
          )}
          <button type="button" onClick={props.onNavigateTasks} className={secondaryControl}>Ver minhas tarefas</button>
        </div>
      </div>

      <div className="mt-4">
        <h3 className="text-[12px] font-semibold text-[var(--fg-secondary)]">Evidências da prioridade</h3>
        <p className="mt-1 text-[11px] leading-5 text-[var(--fg-tertiary)]">Confira os campos do ticket e as relações registradas. Uma fonte vinculada não confirma um acordo de prioridade.</p>
        {evidence.length ? (
          <ul className="mt-2 flex flex-wrap gap-2">
            {evidence.map((item) => (
              <li key={item.key} className="min-w-0 max-w-full">
                <a
                  href={item.href}
                  target={item.kind === "external" ? "_blank" : undefined}
                  rel={item.kind === "external" ? "noopener noreferrer" : undefined}
                  referrerPolicy={item.kind === "external" ? "no-referrer" : undefined}
                  aria-label={item.kind === "external" ? `${item.label} (abre em nova aba)` : undefined}
                  className={`${secondaryControl} max-w-full`}
                >
                  <FileText size={14} className="shrink-0" aria-hidden="true" />
                  <span className="min-w-0 truncate">{item.label}</span>
                  {item.agentProvided && <span className="shrink-0 text-[10px] text-[var(--fg-muted)]">via agente</span>}
                  {item.kind === "external" && <ExternalLink size={12} className="shrink-0" aria-hidden="true" />}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-[12px] text-[var(--fg-tertiary)]">Abra o ticket para conferir prioridade, prazo e contexto registrado.</p>
        )}
      </div>

      {model.waiting && model.nextExecutable && (
        <div className="mt-4 rounded-md border border-[var(--border)] p-3">
          <h3 className="text-[12px] font-semibold text-[var(--fg-secondary)]">Você pode avançar enquanto aguarda</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <TaskLink
              task={model.nextExecutable}
              workspaceSlug={props.workspaceSlug}
              onOpenTask={props.onOpenTask}
              className="min-h-11 min-w-0 flex-1 rounded-md py-1 text-[13px]"
            />
            <button type="button" disabled={saving} onClick={() => props.onConfirm(model.nextExecutable!.id)} className={secondaryControl}>
              Escolher como foco
            </button>
          </div>
        </div>
      )}

      <NextFocusGroups key={paginationKey(props)} {...props} model={model} />
    </>
  );
}

function NextFocusGroups(props: PersonalFocusProps & { model: PersonalFocusModel }) {
  const [limit, setLimit] = useState(NEXT_TASK_PAGE_SIZE);
  const candidates = props.model.nextByProject.flatMap((group) => group.tasks);
  // Keep the supplied stable ordering in each category. A project's backlog
  // must not crowd out another project's current work in the first page.
  const ordered = [
    ...candidates.filter((candidate) => candidate.status !== "backlog"),
    ...candidates.filter((candidate) => candidate.status === "backlog"),
  ];
  const visibleIds = new Set(ordered.slice(0, limit).map((candidate) => candidate.id));
  const visibleGroups = props.model.nextByProject.map((group) => ({
    ...group, tasks: group.tasks.filter((candidate) => visibleIds.has(candidate.id)),
  })).filter((group) => group.tasks.length > 0);
  const total = ordered.length;
  if (!total) return null;
  const shown = Math.min(limit, total);
  return (
    <div className="mt-5">
      <h3 className="text-[13px] font-semibold">{props.model.primary ? "Próximos por projeto" : "Opções por projeto"}</h3>
      <p className="mt-1 text-[11px] leading-5 text-[var(--fg-tertiary)]">
        {props.model.primary
          ? "Escolha outro ticket para trocar o foco. Novas fontes não mudam sua confirmação."
          : "Você pode confirmar um ticket manualmente. Itens em backlog não são sugeridos como próxima ação."}
      </p>
      <div className="mt-3 space-y-4">
        {visibleGroups.map((group) => (
          <div key={group.projectId ?? "unassigned"}>
            <h4 className="mb-1 break-words text-[12px] font-medium text-[var(--fg-tertiary)]">{group.name}</h4>
            <ul className="divide-y divide-[var(--border)]">
              {group.tasks.map((candidate) => (
                <li key={candidate.id} className="flex min-w-0 flex-wrap items-center gap-x-2 py-1">
                  <TaskLink
                    task={candidate}
                    workspaceSlug={props.workspaceSlug}
                    onOpenTask={props.onOpenTask}
                    className="min-h-11 min-w-0 flex-1 rounded-md py-1 text-[12px]"
                    reason={candidate.status === "backlog" ? "Backlog · escolha manual" : undefined}
                  />
                  <button
                    type="button"
                    disabled={props.saving}
                    onClick={() => props.onConfirm(candidate.id)}
                    aria-label={`Escolher ${candidate.identifier} como foco`}
                    className={`${controlClass} px-2 text-[var(--accent)] hover:bg-[var(--bg-hover)]`}
                  >
                    Escolher
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <p role="status" className="min-w-0 flex-1 text-[11px] leading-5 text-[var(--fg-tertiary)]">Mostrando {shown} de {total} tarefas{shown < total ? ` · ${total - shown} restantes` : ""}</p>
        {shown < total && (
          <button type="button" onClick={() => setLimit((current) => current + NEXT_TASK_PAGE_SIZE)} className={secondaryControl}>
            Ver mais tarefas
          </button>
        )}
      </div>
    </div>
  );
}

function ModeBadge({ model, compact = false }: { model: PersonalFocusModel; compact?: boolean }) {
  return (
    <span className={cn("rounded border border-[var(--border)] px-1.5 font-medium text-[var(--fg-secondary)]", compact ? "text-[9px] leading-3" : "py-0.5 text-[10px]")}>
      {model.mode === "confirmed" ? model.primaryOutsideFilters ? "Foco confirmado fora dos filtros" : model.primaryOutsidePeriod ? "Foco confirmado fora do período" : "Confirmado por você" : "Sugestão por regras"}
    </span>
  );
}

function UnavailableNotice() {
  return (
    <p role="status" className="mt-3 text-[12px] leading-5 text-[var(--fg-tertiary)]">
      Seu foco anterior foi concluído, cancelado ou não está mais atribuído ou disponível para você. Confirme um novo ticket para manter seu foco salvo.
    </p>
  );
}

function TaskLink({ task, workspaceSlug, onOpenTask, className, reason, expanded = false, dense = false }: {
  task: FocusTask;
  workspaceSlug: string;
  onOpenTask: (id: string) => void;
  className: string;
  reason?: string;
  expanded?: boolean;
  dense?: boolean;
}) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    onOpenTask(task.id);
  }
  return (
    <a
      href={`/${encodeURIComponent(workspaceSlug)}?issue=${encodeURIComponent(task.id)}`}
      onClick={handleClick}
      className={cn("focus-ring text-[var(--fg)] no-underline hover:text-[var(--accent)]", className)}
      aria-label={`Abrir ${task.identifier}: ${task.title}`}
    >
      <span className={cn("block", !expanded && "truncate")}>
        <span className={cn("font-semibold text-[var(--accent)]", expanded && "mr-2 inline-block rounded bg-[var(--bg-tertiary)] px-1.5 font-mono text-[12px]")}>
          {task.identifier}
        </span>
        <span className={expanded ? "break-words" : "ml-1.5"}>{task.title}</span>
      </span>
      <span className={cn("block text-[var(--fg-muted)]", dense ? "truncate text-[9px] leading-3" : "mt-0.5 text-[10px] leading-4")}>
        <TaskDate value={task.createdAt} label="Criado em" missing="Criação não disponível" />
        <span> · </span>
        <TaskDate value={task.updatedAt} label="Atualizado em" missing="Atualização não disponível" />
      </span>
      {reason && <span className={cn("block truncate text-[var(--fg-tertiary)]", dense ? "text-[9px] leading-3" : "mt-0.5 text-[10px] leading-4")}>{reason}</span>}
    </a>
  );
}

function TaskDate({ value, label, missing }: { value: number | null | undefined; label: string; missing: string }) {
  const formatted = formatTaskDate(value);
  if (formatted === null) return <span>{missing}</span>;
  return <span>{label} <time dateTime={new Date(value!).toISOString()}>{formatted}</time></span>;
}
